/**
 * Starting points for the guided form's "What are you running" card.
 *
 * The same rule as `templates.ts`: every one correct. The list is curated, not
 * a registry search. `popularImages.test.ts` asserts that each entry is pinned,
 * and that the guided draft it produces validates with no errors and no
 * warnings — so an entry that rots fails the build instead of the tenant's
 * first placement.
 *
 * The entries live in `popularImages.data.json` so the weekly refresh job can
 * rewrite pins and dates without touching TypeScript. This module checks that
 * file when it loads: a malformed entry throws in the tests and in the build,
 * never at a tenant.
 *
 * Tags are pinned in the data file and never resolved live (the console does
 * not call a registry). Each was checked against its registry on `verifiedAt`:
 * the pinned tag's digest is the one behind the moving `track.channel` tag.
 *
 * No GPU images: the guided draft cannot request a GPU, so an Ollama or vLLM
 * preset would be placed without one. No secret defaults either: a default
 * password would be written in plaintext into a public manifest. An image that
 * needs a secret names it in `requiredEnv`, and the form refuses to continue
 * until the tenant sets it.
 *
 * Every guided port is exposed to the internet (`global: true`), so no image
 * that serves unauthenticated by default belongs here, and no image that hands
 * admin to its first visitor unless an environment key closes that and is
 * required. Redis is left out for the first reason: the official image runs
 * with no password and protected mode off, and the guided form has no command
 * field to pass `--requirepass`. `popularImages.test.ts` holds the reviewed
 * exposure of each entry.
 */

import { clampDraftResources, type SizeBounds } from './capacityEnvelope'
import { parseImageRef } from './imageRef'
import { DEFAULT_GUIDED_DRAFT, maxBodyError, servesHttpIngress, volumeErrors, type GuidedDraft, type GuidedEnvironment, type GuidedPort, type GuidedVolume } from './guided'
import { isHostProduct, type RentalMode } from './rental'
import { checkS3EnvMapping, type S3EnvMapping } from './s3Connection'
import catalogData from './popularImages.data.json'

export const POPULAR_IMAGE_ICONS = ['globe', 'database', 'zap', 'notebook', 'box', 'chart', 'code', 'search'] as const
export const POPULAR_IMAGE_CATEGORIES = ['web', 'database', 'cache-queue', 'monitoring', 'dev-tools', 'cms', 'storage', 'auth', 'analytics'] as const
export const PUBLISHER_KINDS = ['docker-official', 'verified-publisher', 'project'] as const

export type PopularImageCategory = (typeof POPULAR_IMAGE_CATEGORIES)[number]

export const CATEGORY_LABELS: Record<PopularImageCategory, string> = {
  web: 'Web server',
  database: 'Database',
  'cache-queue': 'Cache and queue',
  monitoring: 'Monitoring',
  'dev-tools': 'Developer tools',
  cms: 'Content management',
  storage: 'Storage',
  auth: 'Authentication',
  analytics: 'Analytics',
}

export interface PopularImage {
  /** Stable, kebab-case. Also the instance name a preset suggests. */
  id: string
  name: string
  /** At most 30 characters, sentence case, no trailing period. */
  description: string
  /** The fallback glyph when there is no logo, or it fails to load. */
  icon: (typeof POPULAR_IMAGE_ICONS)[number]
  /** A fully pinned reference. */
  image: string
  /** What the image listens on; replaces the draft's ports. */
  ports: GuidedPort[]
  /** Defaults only: never a secret, never a placeholder password. */
  env: GuidedEnvironment[]
  /** Keys the image will not start without. Added empty; submit is blocked until set. */
  requiredEnv?: string[]
  /** In draft units (millicores, GB, GB). Never applied to a host product. */
  suggestedSize?: Pick<GuidedDraft, 'cpu' | 'ram' | 'storage'>
  /** The image's official page. */
  docsUrl: string
  category: PopularImageCategory
  /** Search aliases, lower case. */
  keywords: string[]
  /** Stated per entry, never inferred from the repository. */
  publisher: { name: string; kind: (typeof PUBLISHER_KINDS)[number] }
  /** A file under `public/logos/`, or null for the icon. */
  logo: string | null
  /** Shown under "Popular" before any search. */
  featured: boolean
  /**
   * Keeps data on disk. Without `volume` that disk is the ephemeral one the
   * guided SDL requests, and the data is lost when the instance is replaced.
   */
  stateful: boolean
  /**
   * A persistent volume the entry needs, mounted at `mount`. Only on a
   * `stateful` entry: its data then survives restarts and updates, and is
   * deleted when the instance is closed.
   */
  volume?: GuidedVolume
  /**
   * HTTP settings for the entry's TCP port 80, which the host's HTTPS ingress
   * serves. `maxBodyMiB` raises the request-body limit from the SDL default of
   * 1 MiB, up to the Go SDL limit of 100 MiB.
   */
  http?: { maxBodyMiB: number }
  /**
   * TCP ports whose traffic the image leaves unencrypted, where a password is
   * what protects it. Guided ports are public, so the tenant is warned and must
   * acknowledge before continuing (see `plaintextExposure`).
   */
  plaintextPorts?: number[]
  /** Read by the refresh job only: the moving tag that defines "current", and the pinned tags it may move to. */
  track: { channel: string; pattern: string }
  /** When the pin was last checked against its registry. */
  verifiedAt: string
  /** When the registry says the pinned tag was pushed; null where it does not say. */
  upstreamUpdatedAt: string | null
  /** What the pinned tag resolved to on `verifiedAt`, for re-push detection. Never used to pull. */
  digest: string | null
  /**
   * The image's own names for an S3 connection's values, when it does not read
   * the `DDWS_S3_*` contract (DDWS-S3-STORAGE-PLAN §2.3). An S3 connection on
   * Configure writes these as well. Checked here: an unknown placeholder fails.
   */
  s3Env?: S3EnvMapping
}

type Row = Record<string, unknown>

const isRow = (value: unknown): value is Row => typeof value === 'object' && value !== null && !Array.isArray(value)
const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === 'string')
const isDate = (value: unknown): value is string => typeof value === 'string' && !Number.isNaN(Date.parse(value))

/**
 * Check the data file's shape. Hand-written rather than a schema library: the
 * rules that matter (pinned, safe, validates clean) are asserted by the tests,
 * and this only has to make the type honest.
 */
export function parsePopularImages(raw: unknown): PopularImage[] {
  if (!Array.isArray(raw)) throw new Error('popularImages.data.json: expected an array of entries')
  return raw.map((entry: unknown, index) => {
    const where = `popularImages.data.json[${index}]${isRow(entry) && typeof entry.id === 'string' ? ` (${entry.id})` : ''}`
    const fail = (field: string): never => { throw new Error(`${where}: ${field} is missing or malformed`) }
    if (!isRow(entry)) return fail('entry')
    for (const field of ['id', 'name', 'description', 'image', 'docsUrl'] as const) if (typeof entry[field] !== 'string' || entry[field] === '') fail(field)
    if (!POPULAR_IMAGE_ICONS.includes(entry.icon as never)) fail('icon')
    if (!POPULAR_IMAGE_CATEGORIES.includes(entry.category as never)) fail('category')
    if (!Array.isArray(entry.ports) || !entry.ports.every((port) => isRow(port) && Number.isInteger(port.container) && (port.protocol === 'tcp' || port.protocol === 'udp'))) fail('ports')
    if (!Array.isArray(entry.env) || !entry.env.every((row) => isRow(row) && typeof row.key === 'string' && typeof row.value === 'string')) fail('env')
    if (entry.requiredEnv !== undefined && !isStringArray(entry.requiredEnv)) fail('requiredEnv')
    if (entry.suggestedSize !== undefined && !(isRow(entry.suggestedSize) && ['cpu', 'ram', 'storage'].every((key) => typeof (entry.suggestedSize as Row)[key] === 'number'))) fail('suggestedSize')
    if (!isStringArray(entry.keywords) || entry.keywords.length === 0) fail('keywords')
    if (!isRow(entry.publisher) || typeof entry.publisher.name !== 'string' || !PUBLISHER_KINDS.includes(entry.publisher.kind as never)) fail('publisher')
    if (entry.logo !== null && typeof entry.logo !== 'string') fail('logo')
    if (typeof entry.featured !== 'boolean') fail('featured')
    if (typeof entry.stateful !== 'boolean') fail('stateful')
    if (entry.volume !== undefined && !(isRow(entry.volume) && typeof entry.volume.mount === 'string' && typeof entry.volume.sizeGi === 'number' && Object.keys(volumeErrors(entry.volume as unknown as GuidedVolume)).length === 0)) fail('volume')
    // A volume on an entry that does not call itself stateful would contradict the tile, which says whether data is kept.
    if (entry.volume !== undefined && entry.stateful !== true) fail('volume')
    if (entry.http !== undefined && !(isRow(entry.http) && typeof entry.http.maxBodyMiB === 'number' && maxBodyError(entry.http.maxBodyMiB) === null)) fail('http')
    // The limit is only emitted for TCP port 80; on an entry without one it would do nothing.
    if (entry.http !== undefined && !(entry.ports as GuidedPort[]).some(servesHttpIngress)) fail('http')
    if (entry.plaintextPorts !== undefined && !(Array.isArray(entry.plaintextPorts) && entry.plaintextPorts.length > 0 && entry.plaintextPorts.every((port) => Number.isInteger(port)))) fail('plaintextPorts')
    if (!isRow(entry.track) || typeof entry.track.channel !== 'string' || typeof entry.track.pattern !== 'string') fail('track')
    if (!isDate(entry.verifiedAt)) fail('verifiedAt')
    if (entry.upstreamUpdatedAt !== null && !isDate(entry.upstreamUpdatedAt)) fail('upstreamUpdatedAt')
    if (entry.digest !== null && !(typeof entry.digest === 'string' && /^sha256:[0-9a-f]{64}$/.test(entry.digest))) fail('digest')
    if (entry.s3Env !== undefined) checkS3EnvMapping(entry.s3Env, where)
    return entry as unknown as PopularImage
  })
}

export const POPULAR_IMAGES: readonly PopularImage[] = parsePopularImages(catalogData)

/** Collapse whitespace and case, the way both the query and the fields are compared. */
export function normaliseQuery(text: string): string {
  return text.toLowerCase().trim().replace(/\s+/g, ' ')
}

/** The fields a catalog entry is found by: its name, its aliases, and the looser words (category, publisher). */
export interface SearchFields {
  name: string
  keywords: readonly string[]
  secondary: readonly string[]
}

/**
 * How well one entry matches a non-empty, normalised query, or null when it
 * does not. Every term must match some field, and the entry ranks by the best
 * field the whole query matches: 0, its name starts with it; 1, its name or a
 * keyword contains it; 2, a secondary field does; 3, the terms match only
 * separately. Shared by the app search and the SDL template gallery, so the
 * two cannot rank the same words differently.
 */
export function searchRank(q: string, fields: SearchFields): number | null {
  const name = normaliseQuery(fields.name)
  const keywords = fields.keywords.map(normaliseQuery)
  const secondary = fields.secondary.map(normaliseQuery)
  const all = [name, ...keywords, ...secondary]
  if (!q.split(' ').every((term) => all.some((field) => field.includes(term)))) return null
  return name.startsWith(q) ? 0 : [name, ...keywords].some((field) => field.includes(q)) ? 1 : secondary.some((field) => field.includes(q)) ? 2 : 3
}

export function presetSearchFields(preset: PopularImage): SearchFields {
  return { name: preset.name, keywords: preset.keywords, secondary: [CATEGORY_LABELS[preset.category], preset.category, preset.publisher.name] }
}

/**
 * The entries a search shows, best first. Empty: the featured entries, in
 * list order. Otherwise ranked by `searchRank`; ties go to featured entries,
 * then by name.
 */
export function searchPopularImages(query: string, list: readonly PopularImage[] = POPULAR_IMAGES): PopularImage[] {
  const q = normaliseQuery(query)
  if (q === '') return list.filter((preset) => preset.featured)
  const ranked = list.flatMap((preset) => {
    const rank = searchRank(q, presetSearchFields(preset))
    return rank === null ? [] : [{ preset, rank }]
  })
  return ranked
    .sort((a, b) => a.rank - b.rank || Number(b.preset.featured) - Number(a.preset.featured) || a.preset.name.localeCompare(b.preset.name))
    .map(({ preset }) => preset)
}

/**
 * The version a tile shows: the pinned tag shortened to its leading version
 * number (`18.6-alpine` → `18.6`, `v1.54.0` → `1.54.0`). A date tag is already
 * readable and is shown as it is; anything else, the whole tag.
 */
export function presetVersion(preset: Pick<PopularImage, 'image'>): string {
  const tag = parseImageRef(preset.image)?.tag ?? ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(tag)) return tag
  return /^v?(\d+(?:\.\d+)*)/.exec(tag)?.[1] ?? tag
}

/**
 * A draft whose image leaves a public port unencrypted: `ports` are the ones
 * the draft actually exposes, and `key` is what the tenant acknowledges, so a
 * different image or a different port set asks again. Matched by repository,
 * like the required keys, so a hand-typed image is covered too.
 */
export interface PlaintextExposure {
  preset: PopularImage
  ports: number[]
  key: string
}

export function plaintextExposure(draft: Pick<GuidedDraft, 'image' | 'ports'>): PlaintextExposure | null {
  const preset = popularImageForRepository(draft.image)
  if (!preset?.plaintextPorts) return null
  const ports = draft.ports.filter((port) => port.protocol === 'tcp' && preset.plaintextPorts!.includes(port.container)).map((port) => port.container)
  if (ports.length === 0) return null
  return { preset, ports, key: `${repositoryKey(preset.image)}:${ports.join(',')}` }
}

/** The exposure the tenant has not acknowledged yet, or null. */
export function unacknowledgedPlaintext(draft: Pick<GuidedDraft, 'image' | 'ports' | 'acknowledgedPlaintext'>): PlaintextExposure | null {
  const exposure = plaintextExposure(draft)
  return exposure && draft.acknowledgedPlaintext !== exposure.key ? exposure : null
}

function portList(ports: readonly number[]): string {
  return `${ports.length === 1 ? 'port' : 'ports'} ${ports.join(', ')}`
}

/**
 * The words an unencrypted exposure is acknowledged and asked for with, shared
 * by the guided presets and the SDL templates so the two never say it
 * differently.
 */
export function plaintextAcknowledgement(ports: readonly number[]): string {
  return `I understand that traffic on ${portList(ports)} is not encrypted`
}

export function plaintextConfirmError(name: string, ports: readonly number[]): string {
  return `Confirm that ${name} traffic on ${portList(ports)} is not encrypted.`
}

/** Said on a tile before it is chosen. */
export const PLAINTEXT_TILE_NOTE = 'Traffic not encrypted'

/**
 * The warning, shown before the preset is applied and again before signing.
 * It says what a password does and does not do, and never calls the
 * authentication encryption.
 */
export function plaintextWarning({ preset, ports }: Pick<PlaintextExposure, 'preset' | 'ports'>): { title: string; text: string; acknowledge: string } {
  const password = preset.requiredEnv?.[0]
  return {
    title: `${preset.name} traffic is not encrypted`,
    text: `${preset.name} runs with TLS off, and ${portList(ports)} ${ports.length === 1 ? 'is' : 'are'} open to the internet. Anyone on the network path can read or change the queries and results sent over it.${password ? ` ${password} controls who can sign in; it does not encrypt the connection.` : ''}`,
    acknowledge: plaintextAcknowledgement(ports),
  }
}

/**
 * What a stateful entry tells the tenant about its data, in the tile and after
 * it is applied: kept on a volume, or lost with the instance's disk.
 */
export const STATEFUL_NOTE = 'Data is lost if the instance is replaced.'
export const VOLUME_NOTE = 'Data survives restarts and updates. It is deleted when the instance is closed.'

export function statefulNote(preset: Pick<PopularImage, 'stateful' | 'volume'>): string | null {
  if (!preset.stateful) return null
  return preset.volume ? VOLUME_NOTE : STATEFUL_NOTE
}

/** A pin checked this long ago says so on the selected image. Informational only. */
export const STALE_CHECK_DAYS = 90

export function checkIsStale(preset: Pick<PopularImage, 'verifiedAt'>, now: Date): boolean {
  return now.getTime() - Date.parse(preset.verifiedAt) > STALE_CHECK_DAYS * 24 * 60 * 60 * 1000
}

/** The preset whose image the draft runs, if any. Selection is derived, never stored. */
export function popularImageFor(image: string, list: readonly PopularImage[] = POPULAR_IMAGES): PopularImage | undefined {
  const value = image.trim()
  return list.find((preset) => preset.image === value)
}

/** Registry and repository, with Docker Hub's implicit `library/` folded away. */
function repositoryKey(image: string): string | null {
  const ref = parseImageRef(image)
  if (!ref) return null
  const repository = ref.registry === 'docker.io' ? ref.repository.replace(/^library\//, '') : ref.repository
  return `${ref.registry}/${repository}`
}

/**
 * The preset whose repository the draft runs, whatever the tag. This is what
 * the image *needs*, so it is matched loosely: a tenant who typed the image by
 * hand, or moved to a newer tag, still runs an image that will not start (or
 * cannot be reached) without its required keys.
 */
export function popularImageForRepository(image: string, list: readonly PopularImage[] = POPULAR_IMAGES): PopularImage | undefined {
  const key = repositoryKey(image)
  return key === null ? undefined : list.find((preset) => repositoryKey(preset.image) === key)
}

export interface ApplyContext {
  rentalMode: RentalMode | undefined
  bounds: SizeBounds
}

/**
 * Start the draft from a preset.
 *
 * Ports are replaced: they describe what the image listens on, and a stale
 * 8080 beside PostgreSQL is wrong. Environment is merged: rows the tenant
 * typed are theirs and are never overwritten. Size is suggested only where
 * the tenant sizes the machine — a host product is sized by the host. The name
 * follows the preset only while it is still the default.
 */
export function applyPopularImage(draft: GuidedDraft, preset: PopularImage, { rentalMode, bounds }: ApplyContext): GuidedDraft {
  const env = withoutUntouchedPresetEnv(draft, preset)
  const present = new Set(env.map((row) => row.key.trim()))
  for (const row of preset.env) {
    if (present.has(row.key)) continue
    env.push({ ...row })
    present.add(row.key)
  }
  for (const key of preset.requiredEnv ?? []) {
    if (present.has(key)) continue
    env.push({ key, value: '' })
    present.add(key)
  }
  const next: GuidedDraft = {
    ...draft,
    image: preset.image,
    // The body-size limit belongs to the port the ingress serves, so it rides on that port.
    ports: preset.ports.map((port) => preset.http && servesHttpIngress(port) ? { ...port, maxBodyMiB: preset.http.maxBodyMiB } : { ...port }),
    // Replaced like the ports: it describes what the image needs, and a volume
    // left over from another image would be mounted somewhere this one ignores.
    volume: preset.volume ? { ...preset.volume } : undefined,
    env,
    name: draft.name === DEFAULT_GUIDED_DRAFT.name ? preset.id : draft.name,
  }
  const sizesItself = rentalMode === undefined || !isHostProduct(rentalMode)
  return sizesItself && preset.suggestedSize ? clampDraftResources({ ...next, ...preset.suggestedSize }, bounds) : next
}

/**
 * Drop the rows the draft's current preset added and the tenant never
 * changed, before a different preset is applied. A default value beside the
 * wrong image is the same mistake as a stale port, and an empty secret-named
 * key would otherwise reach the manifest with nothing checking it. Rows the
 * tenant typed or edited stay.
 */
function withoutUntouchedPresetEnv(draft: GuidedDraft, next: PopularImage) {
  const current = popularImageFor(draft.image)
  if (!current || current.id === next.id) return [...draft.env]
  const defaults = new Map(current.env.map((row) => [row.key, row.value]))
  const required = new Set(current.requiredEnv ?? [])
  return draft.env.filter((row) => {
    const key = row.key.trim()
    if (defaults.has(key) && defaults.get(key) === row.value) return false
    return !(required.has(key) && row.value.trim() === '')
  })
}

/**
 * Keys the draft's image needs and does not have a value for: `index` is the
 * empty row, or null when the tenant removed it (or never had it, having typed
 * the image by hand). Matched by repository, so a changed tag does not lift it.
 */
export interface MissingEnv {
  index: number | null
  key: string
  preset: PopularImage
}

export function missingRequiredEnv(draft: Pick<GuidedDraft, 'image' | 'env'>): MissingEnv[] {
  const preset = popularImageForRepository(draft.image)
  if (!preset) return []
  return (preset.requiredEnv ?? []).flatMap((key): MissingEnv[] => {
    const index = draft.env.findIndex((row) => row.key.trim() === key)
    if (index === -1) return [{ index: null, key, preset }]
    return draft.env[index]!.value.trim() === '' ? [{ index, key, preset }] : []
  })
}

/**
 * What applying a preset changed besides the image, in the words the Undo
 * line uses: `port 5432`, `POSTGRES_DB=app`. Only what actually differs.
 */
export function describePresetChanges(before: GuidedDraft, after: GuidedDraft): string[] {
  const changes: string[] = []
  const portsChanged = before.ports.length !== after.ports.length || before.ports.some((port, index) => port.container !== after.ports[index]?.container || port.protocol !== after.ports[index]?.protocol)
  if (portsChanged) {
    const list = after.ports.map((port) => port.protocol === 'tcp' ? `${port.container}` : `${port.container}/${port.protocol}`).join(', ')
    changes.push(`${after.ports.length === 1 ? 'port' : 'ports'} ${list}`)
  }
  const bodyLimit = (draft: GuidedDraft) => draft.ports.find((port) => servesHttpIngress(port) && port.maxBodyMiB !== undefined)?.maxBodyMiB
  if (bodyLimit(after) !== undefined && bodyLimit(after) !== bodyLimit(before)) changes.push(`uploads up to ${bodyLimit(after)} MiB on port 80`)
  const had = new Set(before.env.map((row) => `${row.key}=${row.value}`))
  const kept = new Set(after.env.map((row) => row.key))
  for (const row of after.env) {
    if (row.value !== '' && !had.has(`${row.key}=${row.value}`)) changes.push(`${row.key}=${row.value}`)
  }
  const removed = before.env.filter((row) => row.key.trim() !== '' && !kept.has(row.key)).map((row) => row.key)
  if (removed.length > 0) changes.push(`removed ${removed.join(', ')}`)
  if (before.cpu !== after.cpu || before.ram !== after.ram || before.storage !== after.storage) {
    changes.push(`size ${after.cpu}m CPU · ${after.ram} GB RAM · ${after.storage} GB storage`)
  }
  const volumeText = (volume: GuidedVolume | undefined) => volume ? `${volume.sizeGi} GB volume at ${volume.mount}` : ''
  if (volumeText(before.volume) !== volumeText(after.volume)) changes.push(after.volume ? volumeText(after.volume) : 'removed the volume')
  if (before.name !== after.name) changes.push(`name ${after.name}`)
  return changes
}
